import React, {useEffect, useState} from 'react';
import {injectIntl} from 'react-intl';
import axios from 'axios';
import Salary from '@Components/Salary';
import SearchForm from '@Components/SearchForm';
import RelatedJobs from '@Components/RelatedJobs';
import {List} from 'react-content-loader';
import PageHeader from '@Components/PageHeader';
import {Helmet} from 'rnd-helmet';
import {useLocation} from 'react-router';
import {handleSalaryPage} from 'dataLayerHelper';
import {Route} from 'react-router-dom';
import NotFound from '@Pages/NotFound';

const SalaryLocationDetails = ({intl, staticContext, match}) => {
  const transformSalary = (salary) => {
    return salary.replace('$', '').replace(',', '').replace('/yr', '').replace('/hr', '');
  };
  const location = useLocation();
  const dataLayerPageType = 'salary_location_details';
  const dataLayerPageLocation = location.pathname + location.search;
  const [contentLoaded, setContentLoaded] = useState((staticContext && staticContext.data));
  let initialData = {};
  if (staticContext && staticContext.data) {
    initialData = staticContext.data;
  } else if (typeof window !== 'undefined' && window.__ROUTE_DATA__) {
    setContentLoaded(true);
    initialData = window.__ROUTE_DATA__;
    if (process.env.REACT_APP_ENABLE_GTM === 'true') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pageview',
        virtualPage: location.pathname + location.search,
        // Note: relatedJobs will be mutated for having dataLayer properties.
        ...handleSalaryPage(initialData, dataLayerPageType, dataLayerPageLocation),
      });
    }
    delete window.__ROUTE_DATA__;
  }
  const [locationData, setLocationData] = useState(initialData.locationData);
  const [relatedJobsData, setRelatedJobsData] = useState(initialData.relatedJobs);
  const [configurations, setConfigurations] = useState(initialData.configurations);
  const [query, setQuery] = useState(initialData.query);
  const canonicalURL = `${process.env.REACT_APP_BASE_URL}${location.pathname}`;

  useEffect(() => {
    if (!locationData) {
      axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
        callback: '@Callbacks/getJobDetailsForCity',
        currentRoute: {...match, ...{routeName: 'salary-location-details'}},
        data: {},
      }).then((response) => {
        setContentLoaded(true);
        if (response.data) {
          // Mutate data before saving to state.
          handleSalaryPage(response.data, dataLayerPageType, dataLayerPageLocation);
          setRelatedJobsData(response.data.relatedJobs);
          setLocationData(response.data.locationData);
          setConfigurations(response.data.configurations);
          setQuery(response.data.query);
        }
      });
    }
  }, []);

  if (contentLoaded && (!locationData || (Object.getPrototypeOf(locationData) === Object.prototype && Object.keys(locationData).length === 0))) {
    return (
      <Route render={({staticContext}) => {
        if (staticContext) staticContext.status = 404;
        return <NotFound/>;
      }}/>
    );
  }

  if (!locationData) {
    return (
      <>
        <List />
      </>
    );
  }

  const breadcrumbItems = [
    {
      url: `/salary/${match.params.jobTitle}/`,
      title: query?.jobTitle.toLowerCase(),
    },
    {
      url: `/salary/${match.params.jobTitle}/${match.params.location}/`,
      title: `${query?.cityName}, ${query?.stateName}`,
    },
  ];

  const duration = locationData.salary ? (transformSalary(locationData.salary.average) > 1000 ? 'P1Y' : 'P1H') : '';
  const splitted = locationData.location.split(',');
  const cityName = splitted[0];

  const OccupationScript = {
    '@context': 'http://schema.org',
    '@type': 'Occupation',
    'name': locationData.jobTitle,
    'description': locationData.description,
    'estimatedSalary': [
      {
        '@type': 'MonetaryAmountDistribution',
        'name': 'base',
        'currency': 'USD',
        'duration': duration,
        'percentile10': locationData.salary ? transformSalary(locationData.salary.low) : '',
        'percentile90': locationData.salary ? transformSalary(locationData.salary.high) : '',
        'median': locationData.salary ? transformSalary(locationData.salary.average) : '',
      },
    ],
    'occupationLocation': [
      {
        '@type': 'City',
        'name': cityName,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({id: 'SalaryLocation.Page.Meta.Title'}, {
            title: locationData?.jobTitle,
            city: query?.cityName,
            state: query?.stateName,
            sitename: process.env.REACT_APP_SITENAME,
          })}
        </title>
        <link rel="canonical" href={canonicalURL} />
        <meta name="description" content={intl.formatMessage({id: 'SalaryLocation.Page.Meta.Description'}, {
          title: locationData?.jobTitle,
          city: query?.cityName,
          state: query?.stateName,
          salary: locationData.salary.average ?? '',
        })} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{'__html': JSON.stringify(OccupationScript)}}/>
      <PageHeader title={intl.formatMessage({id: 'SalaryLocation.Page.Title'}, {
        title: locationData?.jobTitle,
        city: query?.cityName,
        state: query?.stateName,
      })} breadcrumbItems={breadcrumbItems} />
      <Salary data={locationData} />
      <SearchForm title={locationData?.jobTitle} searchParams={query} baseUrl={`/salary/${match.params.jobTitle}/`} />
      {relatedJobsData && relatedJobsData.results && relatedJobsData.results.length > 0 &&
      <RelatedJobs configurations={configurations} relatedJobsData={relatedJobsData} />}
    </>
  );
};

export default injectIntl(SalaryLocationDetails);
